import { Injectable, OnDestroy, TemplateRef, Type } from '@angular/core';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { Observable, Subject } from 'rxjs';
import { DrawerStateManager } from './drawer/drawer-state.manager';
import { HelpIconManager } from './drawer/help-icon.manager';
import { PersistentStateManager } from './drawer/persistent-state.manager';
import { NavigationHandler } from './drawer/navigation-handler';

@Injectable({
  providedIn: 'root',
})
export class DrawerService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly drawerStateManager: DrawerStateManager,
    private readonly helpIconManager: HelpIconManager,
    private readonly persistentStateManager: PersistentStateManager,
    private readonly navigationHandler: NavigationHandler
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openDrawer<T extends object, P = void>(
    component: Type<T>,
    title: TemplateRef<object> | string,
    width?: number,
    nzClosable = true,
    footer?: TemplateRef<object>,
    params?: P,
    drawerId: string = crypto.randomUUID()
  ): NzDrawerRef {
    const { ref } = this.drawerStateManager.createDrawer(
      component,
      title,
      width,
      nzClosable,
      footer,
      params,
      drawerId
    );

    ref.afterOpen.subscribe(() => {
      this.helpIconManager.addHelpIconToDrawer(drawerId);
    });

    return ref;
  }

  updateDrawerTitle(
    drawerId: string,
    newTitle: string | TemplateRef<object>
  ): void {
    if (this.drawerStateManager.updateTitle(drawerId, newTitle)) {
      this.helpIconManager.addHelpIconToDrawer(drawerId);
    }
  }

  setParams<P>(drawerId: string, params: P): void {
    this.drawerStateManager.setParams(drawerId, params);
  }

  getParams<P>(drawerId: string): Observable<P | null> {
    return this.drawerStateManager.getParams<P>(drawerId);
  }

  getDrawerTitle(drawerId: string): Observable<string | TemplateRef<object>> {
    return this.drawerStateManager.getTitle(drawerId);
  }

  closeDrawer(drawerId: string): void {
    this.drawerStateManager.closeDrawer(drawerId);
  }

  closeAllDrawers(): void {
    this.drawerStateManager.closeAllDrawers();
  }

  getDrawerRef(drawerId: string): NzDrawerRef | null {
    return this.drawerStateManager.getDrawerRef(drawerId);
  }

  goBack(): boolean {
    return this.navigationHandler.goBack();
  }

  setPersistentState<T>(key: string, state: T): void {
    this.persistentStateManager.setState(key, state);
  }

  getPersistentState<T>(key: string): Observable<T> {
    return this.persistentStateManager.getState<T>(key);
  }

  clearPersistentState(key: string): void {
    this.persistentStateManager.clearState(key);
  }
}
